import ko from "knockout";
import { registerKnockoutBinding } from "flow/knockout/register";

registerKnockoutBinding("autogrowTextArea", {
    init(element: HTMLTextAreaElement, valueAccessor: () => any) {
        const enabled = ko.unwrap(valueAccessor()) !== false;
        if (!enabled) {
            return;
        }

        let minHeight = parseFloat(element.style.height);
        if (isNaN(minHeight)) {
            minHeight = 0;
        }

        const shadow: HTMLTextAreaElement = element.cloneNode() as HTMLTextAreaElement;
        shadow.removeAttribute("data-bind");
        shadow.className = element.className;
        shadow.style.visibility = "hidden";
        shadow.style.float = "left";
        shadow.style["z-index" as any] = "-2";
        shadow.style.border = "medium none";
        shadow.style.height = "1px";
        shadow.style.minHeight = "1px";

        element.parentNode.appendChild(shadow);

        function measure(area: HTMLTextAreaElement, shadow: HTMLTextAreaElement) {
            shadow.style.width = area.clientWidth + "px";
            shadow.value = area.value + "...";
            return {
                width: shadow.scrollWidth,
                height: shadow.scrollHeight
            };
        }

        function resize() {
            if (!element.style.minHeight) {
                element.style.minHeight = minHeight + "px";
            }
            const calculatedSize = measure(element, shadow);
            const newHeight = Math.max(minHeight, calculatedSize.height);
            const currentHeight = parseFloat(element.style.height) || 0;
            if (newHeight !== currentHeight) {
                element.style.height = newHeight + "px";
            }
        }

        const unsubscribers: (() => void)[] = [];

        const observe = (event: string, handler: any) => {
            element.addEventListener(event, handler);
            unsubscribers.push(() => element.removeEventListener(event, handler));
        };

        /* 0-timeout to get the already changed text */
        function delayedResize() {
            window.requestAnimationFrame(resize);
        }

        observe("change", resize);
        observe("cut", delayedResize);
        observe("paste", delayedResize);
        observe("drop", delayedResize);
        observe("keydown", delayedResize);
        observe("update.autogrow", resize);

        // If in a table cell and we're the only child, make clicking the cell focus the textarea
        const cell = element.closest("td, th");
        if (cell) {
            const childElements = Array.from(cell.children).filter(child => {
                // Filter out the shadow element
                if (child === shadow) return false;

                // Filter out validation messages
                if (child instanceof HTMLElement && child.classList.contains("input-validation-error-message")) {
                    return false;
                }

                return true;
            });
            const isOnlyChild = childElements.length === 1 && childElements[0] === element;

            if (isOnlyChild) {
                const cellClickHandler = (e: MouseEvent) => {
                    // Only focus if clicking the cell itself, not the textarea
                    if (e.target === cell) {
                        element.focus();
                        // Put cursor at end of text
                        element.setSelectionRange(element.value.length, element.value.length);
                    }
                };
                cell.addEventListener("click", cellClickHandler);
                unsubscribers.push(() => cell.removeEventListener("click", cellClickHandler));
            }
        }

        ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
            unsubscribers.forEach(unsubscribe => unsubscribe());
            shadow.parentNode?.removeChild(shadow);
        });

        delayedResize();
    },
    update(element: HTMLTextAreaElement, valueAccessor: () => any) {
        const enabled = ko.unwrap(valueAccessor()) !== false;
        if (enabled) {
            element.dispatchEvent(new CustomEvent("update.autogrow", { bubbles: false }));
        }
    }
});
